import React, { useState, useEffect } from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import localForage from 'localforage';
import { useBaseUrl } from '../utils/utils';

interface PatientProfile {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    date_of_birth: string;
    gender: string;
    blood_type: string;
    address: {
        street: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
    };
    emergency_contacts: EmergencyContact[];
    preferences: {
        notifications: boolean;
        email_alerts: boolean;
        sms_alerts: boolean;
        language: string;
        timezone: string;
    };
}

interface EmergencyContact {
    id: string;
    name: string;
    relationship: string;
    phone: string;
    email: string;
    is_primary: boolean;
}

const PatientProfile: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [profile, setProfile] = useState<PatientProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [editing, setEditing] = useState(false);
    const [currentTab, setCurrentTab] = useState('personal');
    const [formData, setFormData] = useState<any>({});

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const email = await localForage.getItem<string>('patient_email');
            const response = await fetch(`${baseUrl}patient/api/profile/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: email || ''
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setProfile(data.data.profile);
                setFormData(data.data.profile);
                setLoadError('');
            } else {
                setProfile(null);
                setFormData({ emergency_contacts: [], address: {}, preferences: {} });
                setLoadError(data.message || 'Could not load your profile.');
            }
        } catch (error) {
            console.error('Failed to fetch profile:', error);
            setProfile(null);
            setFormData({ emergency_contacts: [], address: {}, preferences: {} });
            setLoadError('Network error while loading your profile.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    const handleAddressChange = (field: string, value: string) => {
        setFormData({
            ...formData,
            address: {
                ...formData.address,
                [field]: value
            }
        });
    };

    const handlePreferenceChange = (field: string, value: any) => {
        setFormData({
            ...formData,
            preferences: {
                ...formData.preferences,
                [field]: value
            }
        });
    };

    const handleSave = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://64.227.174.0:8004'}/patient/api/profile/update/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: 'patient@example.com',
                    profile_data: formData
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setProfile(formData);
                setEditing(false);
                alert('Profile updated successfully!');
            } else {
                alert('Failed to update profile: ' + data.message);
            }
        } catch (error) {
            console.error('Update error:', error);
            alert('Failed to update profile. Please try again.');
        }
    };

    const addEmergencyContact = () => {
        const newContact: EmergencyContact = {
            id: Date.now().toString(),
            name: '',
            relationship: '',
            phone: '',
            email: '',
            is_primary: false
        };

        setFormData({
            ...formData,
            emergency_contacts: [...formData.emergency_contacts, newContact]
        });
    };

    const removeEmergencyContact = (id: string) => {
        setFormData({
            ...formData,
            emergency_contacts: formData.emergency_contacts.filter((contact: EmergencyContact) => contact.id !== id)
        });
    };

    const updateEmergencyContact = (id: string, field: string, value: string | boolean) => {
        setFormData({
            ...formData,
            emergency_contacts: formData.emergency_contacts.map((contact: EmergencyContact) =>
                contact.id === id ? { ...contact, [field]: value } : contact
            )
        });
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-lg text-gray-600">Loading profile...</span>
                </div>
            </div>
        );
    }

    return (
        <PatientDashboardLayout>
            <div className="max-w-3xl mx-auto px-4 py-8">
                {loadError && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700 mb-6">{loadError}</div>
                )}
                {/* Profile Summary Card */}
                <div className="bg-white rounded-2xl shadow p-6 flex items-center gap-6 mb-8">
                    <img src={`${process.env.PUBLIC_URL}/assets/images/users/avatar-1.jpg`} alt="Avatar" className="w-20 h-20 rounded-full border-4 border-blue-200 shadow" />
                    <div>
                        <div className="text-2xl font-bold text-gray-900 mb-1">My Profile</div>
                        <div className="text-gray-500 mb-2">Manage your personal information and preferences</div>
                        <button className="px-4 py-2 rounded-xl bg-blue-600 text-white font-semibold shadow hover:bg-blue-700" onClick={() => setEditing(true)}>
                            Edit Profile
                        </button>
                    </div>
                </div>
                {/* Tabs */}
                <div className="bg-white rounded-2xl shadow p-4 mb-8">
                    <nav className="flex gap-4 mb-4">
                        <button onClick={() => setCurrentTab('personal')} className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${currentTab === 'personal' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-blue-50'}`}>Personal Information</button>
                        <button onClick={() => setCurrentTab('emergency')} className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${currentTab === 'emergency' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-blue-50'}`}>Emergency Contacts</button>
                        <button onClick={() => setCurrentTab('preferences')} className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${currentTab === 'preferences' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-blue-50'}`}>Preferences</button>
                        <button onClick={() => setCurrentTab('security')} className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${currentTab === 'security' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-blue-50'}`}>Security</button>
                    </nav>
                    <div>
                        {currentTab === 'personal' && (
                            <div className="bg-gray-50 rounded-xl p-6">
                                <form className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="form-group">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                            <input
                                                type="text"
                                                value={formData.first_name || ''}
                                                onChange={(e) => handleInputChange('first_name', e.target.value)}
                                                disabled={!editing}
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                            <input
                                                type="text"
                                                value={formData.last_name || ''}
                                                onChange={(e) => handleInputChange('last_name', e.target.value)}
                                                disabled={!editing}
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                            <input
                                                type="email"
                                                value={formData.email || ''}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                disabled={!editing}
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                            <input
                                                type="tel"
                                                value={formData.phone || ''}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                disabled={!editing}
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                                            <input
                                                type="date"
                                                value={formData.date_of_birth || ''}
                                                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                                                disabled={!editing}
                                                className="form-control"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                                            <select
                                                value={formData.gender || ''}
                                                onChange={(e) => handleInputChange('gender', e.target.value)}
                                                disabled={!editing}
                                                className="form-select"
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Blood Type</label>
                                            <select
                                                value={formData.blood_type || ''}
                                                onChange={(e) => handleInputChange('blood_type', e.target.value)}
                                                disabled={!editing}
                                                className="form-select"
                                            >
                                                <option value="">Select Blood Type</option>
                                                <option value="A+">A+</option>
                                                <option value="A-">A-</option>
                                                <option value="B+">B+</option>
                                                <option value="B-">B-</option>
                                                <option value="AB+">AB+</option>
                                                <option value="AB-">AB-</option>
                                                <option value="O+">O+</option>
                                                <option value="O-">O-</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="pt-6 border-t border-gray-200">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="form-group md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                                                <input
                                                    type="text"
                                                    value={formData.address?.street || ''}
                                                    onChange={(e) => handleAddressChange('street', e.target.value)}
                                                    disabled={!editing}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                                <input
                                                    type="text"
                                                    value={formData.address?.city || ''}
                                                    onChange={(e) => handleAddressChange('city', e.target.value)}
                                                    disabled={!editing}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">State/Province</label>
                                                <input
                                                    type="text"
                                                    value={formData.address?.state || ''}
                                                    onChange={(e) => handleAddressChange('state', e.target.value)}
                                                    disabled={!editing}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Postal Code</label>
                                                <input
                                                    type="text"
                                                    value={formData.address?.postal_code || ''}
                                                    onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                                                    disabled={!editing}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                                                <input
                                                    type="text"
                                                    value={formData.address?.country || ''}
                                                    onChange={(e) => handleAddressChange('country', e.target.value)}
                                                    disabled={!editing}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        )}
                        {currentTab === 'emergency' && (
                            <div className="bg-gray-50 rounded-xl p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-lg font-semibold text-gray-900">Emergency Contacts</h4>
                                    {editing && (
                                        <button
                                            onClick={addEmergencyContact}
                                            className="btn btn-success"
                                        >
                                            Add Contact
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-4">
                                    {formData.emergency_contacts?.map((contact: EmergencyContact, index: number) => (
                                        <div key={contact.id} className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                                            <div className="flex justify-between items-start mb-4">
                                                <h5 className="font-semibold text-gray-900">Contact {index + 1}</h5>
                                                {editing && (
                                                    <button
                                                        onClick={() => removeEmergencyContact(contact.id)}
                                                        className="btn btn-link text-danger p-0"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="form-group">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                    <input
                                                        type="text"
                                                        value={contact.name}
                                                        onChange={(e) => updateEmergencyContact(contact.id, 'name', e.target.value)}
                                                        disabled={!editing}
                                                        className="form-control"
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                                                    <input
                                                        type="text"
                                                        value={contact.relationship}
                                                        onChange={(e) => updateEmergencyContact(contact.id, 'relationship', e.target.value)}
                                                        disabled={!editing}
                                                        className="form-control"
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                                    <input
                                                        type="tel"
                                                        value={contact.phone}
                                                        onChange={(e) => updateEmergencyContact(contact.id, 'phone', e.target.value)}
                                                        disabled={!editing}
                                                        className="form-control"
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                                    <input
                                                        type="email"
                                                        value={contact.email}
                                                        onChange={(e) => updateEmergencyContact(contact.id, 'email', e.target.value)}
                                                        disabled={!editing}
                                                        className="form-control"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <label className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={contact.is_primary}
                                                        onChange={(e) => updateEmergencyContact(contact.id, 'is_primary', Boolean(e.target.checked))}
                                                        disabled={!editing}
                                                        className="form-check-input"
                                                    />
                                                    <span className="text-sm text-gray-700">Primary emergency contact</span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {currentTab === 'preferences' && (
                            <div className="bg-gray-50 rounded-xl p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h4>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h5 className="font-semibold text-gray-900">Push Notifications</h5>
                                            <p className="text-sm text-gray-500">Receive notifications about appointments and updates</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.preferences?.notifications || false}
                                                onChange={(e) => handlePreferenceChange('notifications', e.target.checked)}
                                                disabled={!editing}
                                                className="form-check-input"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">Enabled</span>
                                        </label>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h5 className="font-semibold text-gray-900">Email Alerts</h5>
                                            <p className="text-sm text-gray-500">Receive important updates via email</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.preferences?.email_alerts || false}
                                                onChange={(e) => handlePreferenceChange('email_alerts', e.target.checked)}
                                                disabled={!editing}
                                                className="form-check-input"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">Enabled</span>
                                        </label>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h5 className="font-semibold text-gray-900">SMS Alerts</h5>
                                            <p className="text-sm text-gray-500">Receive appointment reminders via SMS</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.preferences?.sms_alerts || false}
                                                onChange={(e) => handlePreferenceChange('sms_alerts', e.target.checked)}
                                                disabled={!editing}
                                                className="form-check-input"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">Enabled</span>
                                        </label>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="form-group">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                                        <select
                                            value={formData.preferences?.language || 'English'}
                                            onChange={(e) => handlePreferenceChange('language', e.target.value)}
                                            disabled={!editing}
                                            className="form-select"
                                        >
                                            <option value="English">English</option>
                                            <option value="Swahili">Swahili</option>
                                            <option value="French">French</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                                        <select
                                            value={formData.preferences?.timezone || 'Africa/Nairobi'}
                                            onChange={(e) => handlePreferenceChange('timezone', e.target.value)}
                                            disabled={!editing}
                                            className="form-select"
                                        >
                                            <option value="Africa/Nairobi">Africa/Nairobi (GMT+3)</option>
                                            <option value="Africa/Lagos">Africa/Lagos (GMT+1)</option>
                                            <option value="Europe/London">Europe/London (GMT+0)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}
                        {currentTab === 'security' && (
                            <div className="bg-gray-50 rounded-xl p-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h4>
                                <div className="space-y-4">
                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                        <h5 className="font-semibold text-gray-900 mb-2">Change Password</h5>
                                        <p className="text-sm text-gray-500 mb-4">Update your account password for enhanced security</p>
                                        <button className="btn btn-primary">
                                            Change Password
                                        </button>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                        <h5 className="font-semibold text-gray-900 mb-2">Two-Factor Authentication</h5>
                                        <p className="text-sm text-gray-500 mb-4">Add an extra layer of security to your account</p>
                                        <button className="btn btn-success">
                                            Enable 2FA
                                        </button>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                        <h5 className="font-semibold text-gray-900 mb-2">Login History</h5>
                                        <p className="text-sm text-gray-500 mb-4">View recent login activity and device information</p>
                                        <button className="btn btn-outline-secondary">
                                            View History
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientProfile; 